import { Platform, type Insets, type StyleProp, type ViewStyle } from "@-/com.npmjs.react-native";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeTopExtensionContext,
  ScrollEdgeTopInsetContext,
  useScrollEdgeScrollerRegistration,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import { createContext, useContext } from "react";

/**
 * The floating glass chrome's height above every page scroller in this subtree, for a layout
 * whose `PrimaryControlsStripToolbar` is `floating` while its scrollers render in nested routes.
 * A scroller's own `glassHeaderOverlap` takes precedence.
 */
export const GlassHeaderOverlapContext = createContext<number | undefined>(undefined);

/**
 * How far the page content beneath floating glass chrome must sit from the top of its (extended)
 * viewport to clear the chrome at rest, or `undefined` where the chrome is opaque: for sticky
 * offsets and other geometry that follows the content inset.
 */
export function useGlassHeaderInset(glassHeaderOverlap?: number): number | undefined {
  const contextOverlap = useContext(GlassHeaderOverlapContext);
  const topExtension = useContext(ScrollEdgeTopExtensionContext);
  const topInset = useContext(ScrollEdgeTopInsetContext);
  const overlap = glassHeaderOverlap ?? contextOverlap;
  return overlap !== undefined && (isScrollEdgeEffectSupported || Platform.OS === "android")
    ? overlap + topExtension + topInset
    : undefined;
}

/**
 * Where an iOS 26 page scroller runs beneath floating glass chrome (a `PrimaryControlsStripToolbar`
 * with `floating`): the viewport reaches up under the status bar to the window's top edge, the
 * content clears the chrome at rest, and the scroll indicator starts below it. UIKit then blurs
 * whatever scrolls under the chrome (its scroll edge effect), as beneath a system navigation bar,
 * instead of the content dissolving into an opaque band.
 *
 * `glassHeaderOverlap` (or the nearest `GlassHeaderOverlapContext`) is the height of that chrome
 * excluding the safe area: the toolbar row, plus any strip floating with it. `ScrollEdgeViewport`
 * supplies the safe-area clearance separately. Elsewhere, or without either, the viewport is
 * unchanged and `headerOverlap` keeps its meaning.
 */
export function useGlassHeaderViewport({
  glassHeaderOverlap,
  horizontal,
  scrollIndicatorInsets,
  style,
}: {
  glassHeaderOverlap?: number;
  horizontal?: boolean | null;
  scrollIndicatorInsets?: Insets;
  style?: StyleProp<ViewStyle>;
}) {
  const topExtension = useContext(ScrollEdgeTopExtensionContext);
  const inset = useGlassHeaderInset(glassHeaderOverlap);
  const isActive = inset !== undefined && !horizontal;
  // The floating chrome finds the scroller beneath it by geometry; a remount must re-target it.
  useScrollEdgeScrollerRegistration(isActive && isScrollEdgeEffectSupported);
  if (!isActive) {
    return { headerOverlap: undefined, isActive, scrollIndicatorInsets, style };
  }
  const headerOverlap = inset;
  return {
    headerOverlap,
    isActive,
    scrollIndicatorInsets: { ...scrollIndicatorInsets, top: headerOverlap },
    // A negative margin grows a flex (or inset-pinned) viewport upward by the same amount, so its
    // bottom edge stays where it was.
    style: topExtension === 0 ? style : [style, { marginTop: -topExtension }],
  };
}
